import React, { useState, useEffect, useMemo, useRef } from "react";
import { format, addDays, subDays, startOfWeek } from "date-fns";
import firebase from "../../firebase";
import { getVendorId } from "../../utils/rbac";
import WalkInForm from "../WalkIn/walkinform";
import "./stylistcalendar.css";

const db = () => firebase.firestore();

// ── Status config (4 real + computed In Progress) ─────────────────────────
const STATUS_CFG = {
  Completed:     { bg: "#e8f5e9", color: "#1b5e20", border: "#43a047", label: "Completed"   },
  "In Progress": { bg: "#e3f2fd", color: "#0d47a1", border: "#1976d2", label: "In Progress" },
  Accepted:      { bg: "#e8f4fd", color: "#01579b", border: "#03a9f4", label: "Upcoming"    },
  Pending:       { bg: "#fff8e1", color: "#c77700", border: "#ffc107", label: "Pending"     },
  Cancelled:     { bg: "#fafafa", color: "#9e9e9e", border: "#bdbdbd", label: "Cancelled"   },
};

const LEGEND = [
  { key: "Completed",    dot: "#43a047", label: "Completed"   },
  { key: "In Progress",  dot: "#1976d2", label: "In Progress" },
  { key: "Accepted",     dot: "#03a9f4", label: "Upcoming"    },
  { key: "Pending",      dot: "#ffc107", label: "Pending"     },
  { key: "Cancelled",    dot: "#bdbdbd", label: "Cancelled"   },
];

const HOUR_H     = 120;
const MIN_PX     = HOUR_H / 60;
const MIN_BLOCK  = 22;   // minimum block height (px) — keeps very short appts readable
const COL_W      = 165;
const TIME_W     = 70;
const TOTAL_MINS = 1440;
const REFRESH_MS = 10 * 60 * 1000; // 10 minutes

// Horizontal (transposed) Gantt view
const H_HOUR_W = 120;         // px per hour (full 24-h span)
const H_MIN_PX = H_HOUR_W / 60;
const ROW_H    = 88;           // px per stylist row
const SNAME_W  = 160;          // sticky name column width
const H_HDR_H  = 48;           // time header row height

// ── Helpers ────────────────────────────────────────────────────────────────
const toMin = (t) => {
  if (!t) return 0;
  const [h, m] = String(t).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${ap}`;
};

const addMinutes = (timeStr, mins) => {
  const total = toMin(timeStr) + mins;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const apptDuration = (b) => {
  const sum = (b.services || []).reduce((s, sv) => s + parseInt(sv.time || 0, 10), 0);
  return sum > 0 ? sum : 60;
};

// Compute overlap column layout for vertical calendar blocks.
// Returns a Map: booking key -> { col, colCount }
const computeOverlapLayout = (bookings) => {
  const items = bookings.map((b, i) => ({
    b, i,
    start: toMin(b.scheduleTime),
    end:   toMin(b.scheduleTime) + apptDuration(b),
    key:   b.bookingId || b._docId || String(i),
    col: 0, colCount: 1,
  })).sort((a, b) => a.start - b.start || b.end - a.end);

  const colEnds = [];
  items.forEach((item) => {
    let placed = false;
    for (let c = 0; c < colEnds.length; c++) {
      if (colEnds[c] <= item.start) {
        colEnds[c] = item.end;
        item.col = c;
        placed = true;
        break;
      }
    }
    if (!placed) {
      item.col = colEnds.length;
      colEnds.push(item.end);
    }
  });

  items.forEach((item) => {
    let maxCol = item.col;
    items.forEach((other) => {
      if (other === item) return;
      if (other.start < item.end && other.end > item.start) {
        maxCol = Math.max(maxCol, other.col);
      }
    });
    item.colCount = maxCol + 1;
  });

  const result = new Map();
  items.forEach((item) => result.set(item.key, { col: item.col, colCount: item.colCount }));
  return result;
};

const weekDays = (anchor) => {
  const mon = startOfWeek(anchor, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
};

/**
 * Compute display status from 4 real Firestore statuses.
 * In Progress = Accepted/Pending whose time window spans now (today only).
 */
const computeStatus = (b, selStr) => {
  const raw = b.status;
  if (raw === "Cancelled" || raw === "Canceled") return "Cancelled";
  if (raw === "Completed") return "Completed";
  const today = format(new Date(), "yyyy-MM-dd");
  if (selStr === today && (raw === "Accepted" || raw === "Pending")) {
    const now    = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const startM = toMin(b.scheduleTime);
    const endM   = startM + apptDuration(b);
    if (nowMin >= startM && nowMin < endM) return "In Progress";
  }
  return raw;
};

/** True if current datetime is at or after the appointment start (allow Mark Done). */
const canMarkDone = (b) => {
  const parts = (b.scheduleDate || "").split("-");
  if (parts.length < 3) return true;
  const [y, mo, d] = parts.map(Number);
  const [hr, mn]   = (b.scheduleTime || "00:00").split(":").map(Number);
  return new Date() >= new Date(y, mo - 1, d, hr, mn);
};

/** Format "yyyy-MM-dd" -> "May, 05 Mon" */
const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr + "T12:00:00");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const days   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return `${months[dt.getMonth()]}, ${String(dt.getDate()).padStart(2,"0")} ${days[dt.getDay()]}`;
};

// ── SMS / Email (auto-triggered on status change, mirrors Flutter OrderCard) ─
const SMS_KEY       = "36947CFC979FE1";
const SMS_SENDER_ID = "ZCEIGT";

const sendSmsToUser = async (b, newStatus) => {
  const phone = b.phone || b.userPhone;
  if (!phone) return;
  const salonName = b.salonName || "";
  const date      = b.scheduleDate || "";
  const time      = b.scheduleTime || "";
  const firstName = (b.userName || "there").trim().split(/\s+/)[0];
  let message, templateId;
  if (newStatus === "Accepted") {
    message    = `Hurray, your booking request with Salon - ${salonName} on ${date} at ${time} is confirmed. See you soon! – ZC9Eight`;
    templateId = "1707176684031372734";
  } else if (newStatus === "Completed") {
    message    = `Your appointment with ${salonName} Salon is completed. Hope you loved it! Please rate your experience on ZC9Eight.`;
    templateId = "1707176684047152806";
  } else if (newStatus === "Cancelled") {
    message    = `Hi ${firstName}, We regret to inform you that ${salonName} has cancelled your appointment scheduled on ${date} at ${time}. Please rebook another slot at your convenience - ZC9Eight`;
    templateId = "1707176684171163018";
  } else {
    return;
  }
  const url = `http://msg.pwasms.com/app/smsapi/index.php?key=${SMS_KEY}&campaign=0&senderid=${SMS_SENDER_ID}&routeid=9&type=text&contacts=${encodeURIComponent(phone)}&msg=${encodeURIComponent(message)}&template_id=${templateId}`;
  try { await fetch(url, { mode: "no-cors" }); } catch (_) { /* silent */ }
};

const sendEmailOnStatusChange = async (b, newStatus) => {
  // Calls Firebase Cloud Function "sendBookingEmail" – deploy it to enable.
  // Falls back silently if the function is not yet deployed.
  try {
    const fn = firebase.functions().httpsCallable("sendBookingEmail");
    await fn({
      newStatus,
      bookingId:    b.bookingId    || b._docId || "",
      userEmail:    b.userEmail    || b.email  || "",
      userName:     b.userName     || "",
      salonName:    b.salonName    || "",
      stylistName:  b.stylistName  || "",
      scheduleDate: b.scheduleDate || "",
      scheduleTime: b.scheduleTime || "",
      finalPrice:   String(b.finalPrice || ""),
      phone:        b.phone        || "",
    });
  } catch (_) { /* Cloud Function not deployed or failed – silent */ }
};

// ══════════════════════════════════════════════════════════════════════════
// Payment Confirm Modal  (shown when vendor clicks "Mark Done")
// ══════════════════════════════════════════════════════════════════════════
const PAYMENT_MODES = ["Cash", "UPI", "Card", "Online", "Other"];

const PaymentConfirmModal = ({ booking: b, initialMode, saving, onCancel, onConfirm }) => {
  const [payMode, setPayMode] = useState(initialMode);

  const dur  = apptDuration(b);
  const endT = addMinutes(b.scheduleTime, dur);

  return (
    <div className="sc_confirm_overlay" onClick={onCancel}>
      <div className="sc_pcm" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="sc_pcm_header">
          <i className="fa fa-flag-checkered sc_pcm_hdr_icon"></i>
          <span className="sc_pcm_hdr_title">Mark as Completed</span>
        </div>

        {/* Appointment summary */}
        <div className="sc_pcm_appt">
          <div className="sc_pcm_name">{b.userName || "—"}</div>
          <div className="sc_pcm_meta">
            <span><i className="fa fa-clock-o"></i> {fmt12(b.scheduleTime)} – {fmt12(endT)}</span>
            <span><i className="fa fa-scissors"></i> {b.stylistName || "—"}</span>
            {b.finalPrice ? <span><i className="fa fa-inr"></i> ₹{b.finalPrice}</span> : null}
          </div>
        </div>

        {/* Payment mode selector */}
        <div className="sc_pcm_body">
          <div className="sc_pcm_pay_lbl">Select Payment Mode</div>
          <div className="sc_pcm_pay_grid">
            {PAYMENT_MODES.map((m) => (
              <button
                key={m}
                className={"sc_pcm_pay_opt" + (payMode === m ? " selected" : "")}
                onClick={() => setPayMode(m)}
              >
                <i className={`fa ${
                  m === "Cash"   ? "fa-money"
                  : m === "UPI"  ? "fa-mobile"
                  : m === "Card" ? "fa-credit-card"
                  : m === "Online" ? "fa-globe"
                  : "fa-ellipsis-h"
                }`}></i>
                {m}
              </button>
            ))}
          </div>
          <div className="sc_pcm_note">
            <i className="fa fa-info-circle"></i>
            Payment status will be set to <strong>Paid</strong> and appointment marked <strong>Completed</strong>.
          </div>
        </div>

        {/* Actions */}
        <div className="sc_pcm_actions">
          <button className="sc_confirm_btn sc_confirm_cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="sc_confirm_btn sc_confirm_ok sc_confirm_ok_orange"
            onClick={() => onConfirm(payMode)}
            disabled={saving}
          >
            {saving
              ? <><i className="fa fa-spinner fa-pulse"></i> Saving…</>
              : <><i className="fa fa-check"></i> Confirm &amp; Complete</>}
          </button>
        </div>

      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// Call Booking Modal
// ══════════════════════════════════════════════════════════════════════════
const CallBookingModal = ({ vendorId, allStylists, initialStylist, initialDate, initialTime, existingBookings, onClose, onSaved }) => {
  // Customer
  const [name,       setName]       = useState("");
  const [phone,      setPhone]      = useState("");
  const [phoneErr,   setPhoneErr]   = useState("");
  const [sugg,       setSugg]       = useState([]);
  const [showSugg,   setShowSugg]   = useState(false);
  const sugRef = useRef(null);

  // Services
  const [services,    setServices]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [selCat,      setSelCat]      = useState("");
  const [svcSearch,   setSvcSearch]   = useState("");
  const [selSvcs,     setSelSvcs]     = useState([]);
  const [editPrices,  setEditPrices]  = useState({});
  const [svcStylist,  setSvcStylist]  = useState({}); // serviceId -> stylist obj

  // Schedule
  const [bDate,    setBDate]    = useState(initialDate);
  const [bTime,    setBTime]    = useState(initialTime);
  const [bStylist, setBStylist] = useState(initialStylist || null);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  // ── Fetch services ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) return;
    const unsub = db()
      .collection("services")
      .where("vendorId", "==", vendorId)
      .orderBy("categoryName")
      .onSnapshot((snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setServices(docs);
        const cats = [...new Set(docs.map((d) => d.categoryName).filter(Boolean))];
        setCategories(cats);
        setSelCat((prev) => prev || cats[0] || "");
      });
    return () => unsub();
  }, [vendorId]); // eslint-disable-line

  // ── Outside click hides suggestions ──────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (sugRef.current && !sugRef.current.contains(e.target)) setShowSugg(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Customer search ───────────────────────────────────────────────────────
  const searchCustomers = async (q) => {
    if (q.length < 2) { setSugg([]); setShowSugg(false); return; }
    const isPhone = /^\d+$/.test(q);
    let snap;
    if (isPhone) {
      snap = await db().collection("customers")
        .where("vendors", "array-contains", vendorId)
        .where("phone", ">=", q).where("phone", "<", q + "z")
        .limit(8).get();
    } else {
      const lower = q.toLowerCase();
      snap = await db().collection("customers")
        .where("vendors", "array-contains", vendorId)
        .where("nameLower", ">=", lower).where("nameLower", "<", lower + "z")
        .limit(8).get();
      if (snap.empty) {
        const fallback = await db().collection("customers")
          .where("vendors", "array-contains", vendorId).limit(50).get();
        const filtered = fallback.docs.filter(
          (d) => (d.data().name || "").toLowerCase().includes(lower)
        );
        setSugg(filtered.map((d) => ({ id: d.id, ...d.data() })));
        setShowSugg(true);
        return;
      }
    }
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setSugg(list);
    setShowSugg(list.length > 0);
  };

  const selectCustomer = (c) => {
    setName(c.name || "");
    setPhone(c.phone || "");
    setShowSugg(false);
    setSugg([]);
  };

  // ── Service toggle ────────────────────────────────────────────────────────
  const filteredSvcs = svcSearch.trim()
    ? services.filter((s) =>
        (s.serviceName || "").toLowerCase().includes(svcSearch.toLowerCase()) ||
        (s.categoryName || "").toLowerCase().includes(svcSearch.toLowerCase()))
    : services.filter((s) => s.categoryName === selCat);

  const isSel = (id) => selSvcs.some((s) => s.serviceId === id);

  const toggleSvc = (svc) => {
    const id = svc.serviceId || svc.id;
    if (isSel(id)) {
      setSelSvcs((p) => p.filter((s) => s.serviceId !== id));
      setSvcStylist((p) => { const n = { ...p }; delete n[id]; return n; });
      setEditPrices((p) => { const n = { ...p }; delete n[id]; return n; });
    } else {
      setSelSvcs((p) => [...p, { ...svc, serviceId: id }]);
      setEditPrices((p) => ({ ...p, [id]: String(svc.offerPrice || svc.price || 0) }));
      setSvcStylist((p) => ({ ...p, [id]: bStylist || null }));
    }
  };

  const totalPrice = selSvcs.reduce(
    (sum, s) => sum + parseInt(editPrices[s.serviceId] || s.offerPrice || s.price || 0, 10), 0
  );
  const totalTime = selSvcs.reduce(
    (sum, s) => sum + parseInt(s.serviceTime || s.time || 0, 10), 0
  );

  // ── Overlap check ────────────────────────────────────────────────────────
  const overlapWarning = useMemo(() => {
    if (!bTime || !bDate || selSvcs.length === 0) return null;
    const dur      = totalTime > 0 ? totalTime : 60;
    const newStart = toMin(bTime);
    const newEnd   = newStart + dur;
    const involvedIds = new Set(
      selSvcs.map((s) => svcStylist[s.serviceId]?.id).filter(Boolean)
    );
    if (involvedIds.size === 0) return null;
    const clashes = (existingBookings || []).filter((b) => {
      if (b.status === "Cancelled" || b.status === "Canceled") return false;
      if (b.scheduleDate !== bDate) return false;
      const bIds = b.stylistIds?.length ? b.stylistIds : [b.stylistId].filter(Boolean);
      if (!bIds.some((id) => involvedIds.has(id))) return false;
      const s = toMin(b.scheduleTime);
      const e = s + apptDuration(b);
      return newStart < e && newEnd > s;
    });
    return clashes.length > 0
      ? clashes.map((b) => `${b.userName || "—"} @ ${fmt12(b.scheduleTime)}`).join(", ")
      : null;
  }, [bTime, bDate, totalTime, selSvcs, svcStylist, existingBookings]); // eslint-disable-line

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const pErr = phone.replace(/\D/g, "").length !== 10
      ? (phone.replace(/\D/g, "").length === 0 ? "Enter phone number" : "Must be 10 digits")
      : "";
    if (pErr) { setPhoneErr(pErr); return; }
    setPhoneErr("");
    if (!name.trim())         { setError("Enter customer name"); return; }
    if (!bDate)               { setError("Select a date"); return; }
    if (!bTime)               { setError("Select a time"); return; }
    if (selSvcs.length === 0) { setError("Select at least one service"); return; }
    const unassigned = selSvcs.filter((s) => !svcStylist[s.serviceId]);
    if (unassigned.length)    {
      setError(`Assign stylist to: ${unassigned.map((s) => s.serviceName).join(", ")}`);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const svcList = selSvcs.map((s) => ({
        name:        s.serviceName,
        price:       parseInt(editPrices[s.serviceId] || s.offerPrice || s.price || 0, 10),
        time:        s.serviceTime || s.time || 0,
        subCatId:    s.serviceId,
        vendorId:    s.vendorId,
        stylistId:   svcStylist[s.serviceId]?.id,
        stylistName: svcStylist[s.serviceId]?.name,
      }));

      const breakdown = {};
      svcList.forEach((s) => {
        if (!breakdown[s.stylistId]) {
          breakdown[s.stylistId] = { name: s.stylistName, revenue: 0, serviceCount: 0 };
        }
        breakdown[s.stylistId].revenue      += s.price;
        breakdown[s.stylistId].serviceCount += 1;
      });
      const stylistIds     = Object.keys(breakdown);
      const primaryStylist = svcStylist[selSvcs[0].serviceId];

      const vendorDoc = await db().collection("vendors").doc(vendorId).get();
      const vendor    = vendorDoc.exists ? vendorDoc.data() : {};

      const bookingId = `ZC${Date.now()}`;
      const docRef    = db().collection("appointments").doc();

      const apptData = {
        bookingId,
        id:             docRef.id,
        isWalkIn:       false,
        isCallBooking:  true,

        userName:  name.trim(),
        phone:     phone.trim(),
        userId:    phone.trim(),

        vendorId,
        salonName:    vendor.salonName    || "",
        salonAddress: vendor.salonAddress || "",

        stylistId:        primaryStylist?.id   || "",
        stylistName:      primaryStylist?.name || "",
        stylistIds,
        stylistBreakdown: breakdown,

        services:          svcList,
        TotalServicePrice: totalPrice,
        finalPrice:        totalPrice,

        scheduleDate: bDate,
        scheduleTime: bTime,
        createdAt:    bDate,
        record_timestamp: firebase.firestore.FieldValue.serverTimestamp(),

        status:        "Accepted",
        paymentMode:   "Cash",
        paymentStatus: "Pending",

        homeOrder:      false,
        discountAmount: 0,
        couponCode:     null,
      };

      await docRef.set(apptData);

      // Upsert customer
      const custRef  = db().collection("customers").doc(phone.trim());
      const custSnap = await custRef.get();
      const vStats   = {
        vendorStats: {
          [vendorId]: {
            visits:    firebase.firestore.FieldValue.increment(1),
            spent:     firebase.firestore.FieldValue.increment(0),
            lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
          },
        },
      };
      if (custSnap.exists) {
        await custRef.set({
          name:       name.trim(),
          phone:      phone.trim(),
          lastVisit:  firebase.firestore.FieldValue.serverTimestamp(),
          visitCount: firebase.firestore.FieldValue.increment(1),
          vendors:    firebase.firestore.FieldValue.arrayUnion(vendorId),
          ...vStats,
        }, { merge: true });
      } else {
        await custRef.set({
          customerId: phone.trim(),
          name:       name.trim(),
          nameLower:  name.trim().toLowerCase(),
          phone:      phone.trim(),
          createdAt:  firebase.firestore.FieldValue.serverTimestamp(),
          lastVisit:  firebase.firestore.FieldValue.serverTimestamp(),
          totalSpent: 0,
          visitCount: 1,
          source:     "call_booking",
          vendors:    [vendorId],
          vendorStats: {
            [vendorId]: { visits: 1, spent: 0, lastVisit: firebase.firestore.FieldValue.serverTimestamp() },
          },
        });
      }

      onSaved({ ...apptData, _docId: docRef.id });
    } catch (e) {
      setError("Failed to save booking. Please try again.");
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="sc_modal_overlay" onClick={onClose}>
      <div className="sc_cbm" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="sc_cbm_header">
          <i className="fa fa-phone sc_cbm_hdr_icon"></i>
          <span className="sc_cbm_hdr_title">Book Call Appointment</span>
          <button className="sc_modal_close" onClick={onClose}>×</button>
        </div>

        <div className="sc_cbm_body">

          {/* ── LEFT: customer + schedule + summary ── */}
          <div className="sc_cbm_left">

            <div className="sc_cbm_sec_lbl"><i className="fa fa-user"></i> Customer</div>

            {/* Name with autocomplete */}
            <div className="sc_cbm_field" ref={sugRef} style={{ position: "relative" }}>
              <label>Name</label>
              <input
                placeholder="Customer name"
                value={name}
                onChange={(e) => { setName(e.target.value); searchCustomers(e.target.value); }}
              />
              {showSugg && sugg.length > 0 && (
                <div className="sc_cbm_sugg">
                  {sugg.map((c) => (
                    <div key={c.id} className="sc_cbm_sugg_item" onClick={() => selectCustomer(c)}>
                      <strong>{c.name}</strong>
                      <span>{c.phone}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Phone */}
            <div className="sc_cbm_field">
              <label>Phone</label>
              <input
                placeholder="10-digit mobile"
                value={phone}
                maxLength={10}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/\D/g, ""));
                  setPhoneErr("");
                }}
              />
              {phoneErr && <span className="sc_cbm_err">{phoneErr}</span>}
            </div>

            <div className="sc_cbm_sec_lbl" style={{ marginTop: 14 }}>
              <i className="fa fa-calendar"></i> Schedule
            </div>

            <div className="sc_cbm_row2">
              <div className="sc_cbm_field">
                <label>Date</label>
                <input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} />
              </div>
              <div className="sc_cbm_field">
                <label>Time</label>
                <input type="time" value={bTime} onChange={(e) => setBTime(e.target.value)} />
              </div>
            </div>

            <div className="sc_cbm_sec_lbl" style={{ marginTop: 14 }}>
              <i className="fa fa-scissors"></i> Default Stylist
            </div>

            <div className="sc_cbm_field">
              <select
                value={bStylist?.id || ""}
                onChange={(e) => {
                  const st = allStylists.find((s) => s.id === e.target.value) || null;
                  setBStylist(st);
                  if (st) {
                    const updated = {};
                    selSvcs.forEach((s) => { updated[s.serviceId] = st; });
                    setSvcStylist((prev) => ({ ...prev, ...updated }));
                  }
                }}
              >
                <option value="">— Select stylist —</option>
                {allStylists.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Selected services summary */}
            {selSvcs.length > 0 && (
              <div className="sc_cbm_summary">
                <div className="sc_cbm_summary_hdr">
                  Selected ({selSvcs.length}) · ₹{totalPrice} · ~{totalTime} min
                </div>
                {selSvcs.map((s) => (
                  <div key={s.serviceId} className="sc_cbm_summary_row">
                    <span className="sc_cbm_sname">{s.serviceName}</span>
                    <div className="sc_cbm_summary_right">
                      <select
                        className="sc_cbm_mini_sel"
                        value={svcStylist[s.serviceId]?.id || ""}
                        onChange={(e) => {
                          const st = allStylists.find((x) => x.id === e.target.value) || null;
                          setSvcStylist((p) => ({ ...p, [s.serviceId]: st }));
                        }}
                      >
                        <option value="">Stylist</option>
                        {allStylists.map((st) => (
                          <option key={st.id} value={st.id}>{st.name}</option>
                        ))}
                      </select>
                      <div className="sc_cbm_price_wrap">
                        <span>₹</span>
                        <input
                          className="sc_cbm_price_input"
                          type="number"
                          min="0"
                          value={editPrices[s.serviceId] || "0"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditPrices((p) => ({ ...p, [s.serviceId]: val }));
                          }}
                        />
                      </div>
                      <button className="sc_cbm_remove_svc" onClick={() => toggleSvc(s)}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {overlapWarning && (
              <div className="sc_cbm_overlap_warn">
                <i className="fa fa-exclamation-triangle"></i>
                &nbsp;Overlap: {overlapWarning}
              </div>
            )}
            {error && <div className="sc_cbm_error">{error}</div>}
          </div>

          {/* ── RIGHT: service picker ── */}
          <div className="sc_cbm_right">
            <div className="sc_cbm_sec_lbl"><i className="fa fa-list"></i> Services</div>

            <div className="sc_cbm_svc_search">
              <i className="fa fa-search"></i>
              <input
                placeholder="Search services…"
                value={svcSearch}
                onChange={(e) => setSvcSearch(e.target.value)}
              />
              {svcSearch && (
                <button className="sc_cbm_search_clear" onClick={() => setSvcSearch("")}>×</button>
              )}
            </div>

            {!svcSearch && (
              <div className="sc_cbm_cats">
                {categories.map((c) => (
                  <button
                    key={c}
                    className={"sc_cbm_cat" + (selCat === c ? " active" : "")}
                    onClick={() => setSelCat(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            <div className="sc_cbm_svc_list">
              {filteredSvcs.length === 0 ? (
                <div className="sc_cbm_svc_empty">No services found</div>
              ) : (
                filteredSvcs.map((svc) => {
                  const id  = svc.serviceId || svc.id;
                  const sel = isSel(id);
                  return (
                    <div
                      key={id}
                      className={"sc_cbm_svc_row" + (sel ? " selected" : "")}
                      onClick={() => toggleSvc(svc)}
                    >
                      <div className="sc_cbm_svc_check">
                        {sel
                          ? <i className="fa fa-check-circle" style={{ color: "#43a047" }}></i>
                          : <i className="fa fa-circle-o" style={{ color: "#bdbdbd" }}></i>}
                      </div>
                      <div className="sc_cbm_svc_info">
                        <div className="sc_cbm_svc_name">{svc.serviceName}</div>
                        {svc.group && (
                          <div className="sc_cbm_svc_group">{svc.group}</div>
                        )}
                      </div>
                      <div className="sc_cbm_svc_meta">
                        <span className="sc_cbm_svc_price">₹{svc.offerPrice || svc.price || 0}</span>
                        <span className="sc_cbm_svc_time">{svc.serviceTime || svc.time || 0}m</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>{/* end sc_cbm_body */}

        {/* Footer */}
        <div className="sc_cbm_footer">
          <button className="sc_cbm_cancel_btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="sc_cbm_save_btn" onClick={handleSave} disabled={saving}>
            {saving
              ? <><i className="fa fa-spinner fa-pulse"></i>&nbsp;Saving…</>
              : <><i className="fa fa-calendar-check-o"></i>&nbsp;Book Appointment</>}
          </button>
        </div>

      </div>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const StylistCalendar = () => {
  const vendorId = getVendorId();

  const [anchor, setAnchor]     = useState(new Date());
  const [selDate, setSelDate]   = useState(new Date());
  const [stylists, setStylists] = useState([]);
  const [workInfo, setWorkInfo] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);   // bump to re-fetch

  // Hover tooltip
  const [tooltip, setTooltip]   = useState(null);
  // Action modal (click on appointment block)
  const [actionModal, setActionModal]   = useState(null); // {b, status}
  // Confirm dialog (inside action modal)
  const [confirmModal, setConfirmModal] = useState(null); // {action, b, title, msg}
  const [saving, setSaving]             = useState(false);
  // Toast notification
  const [notification, setNotification] = useState(null); // {msg, type}
  // Call booking modal
  const [callModal,          setCallModal]          = useState(null); // { stylist, time, date }
  const [bookingTypePicker,  setBookingTypePicker]  = useState(null); // { stylist, time, date }
  const [showWalkInModal,    setShowWalkInModal]    = useState(false);
  // Payment modal (shown before marking Completed)
  const [paymentModal, setPaymentModal] = useState(null); // { b }
  // Transpose (horizontal Gantt) toggle
  const [transposed, setTransposed] = useState(false);
  const hScrollRef = useRef(null);

  const gridRef = useRef(null);

  // ── Auto-dismiss toast ────────────────────────────────────────────────────
  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 3500);
    return () => clearTimeout(t);
  }, [notification]);

  // ── Auto-refresh timer ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setRefreshKey((k) => k + 1), REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // ── Data fetches ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) return;
    return db().collection("stylists")
      .where("vendorId", "==", vendorId)
      .where("active", "==", true)
      .onSnapshot((snap) =>
        setStylists(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      );
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    db().collection("workDetails")
      .where("vendorId", "==", vendorId).limit(1).get()
      .then((snap) =>
        setWorkInfo(snap.empty
          ? { opening_time: "09:00", closing_time: "21:00" }
          : snap.docs[0].data())
      );
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    const days     = weekDays(anchor);
    const startStr = format(days[0], "yyyy-MM-dd");
    const endStr   = format(days[6], "yyyy-MM-dd");
    db().collection("appointments")
      .where("vendorId", "==", vendorId)
      .get()
      .then((snap) => {
        setBookings(
          snap.docs
            .map((d) => ({ _docId: d.id, ...d.data() }))
            .filter((b) => b.scheduleDate >= startStr && b.scheduleDate <= endStr)
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId, anchor, refreshKey]); // refreshKey triggers re-fetch

  // ── Derived values ────────────────────────────────────────────────────────
  const openMin  = useMemo(() => toMin(workInfo?.opening_time  ?? "09:00"), [workInfo]);
  const closeMin = useMemo(() => toMin(workInfo?.closing_time  ?? "21:00"), [workInfo]);
  const totalH   = TOTAL_MINS * MIN_PX;

  const hourSlots = useMemo(() => Array.from({ length: 24 }, (_, i) => i * 60), []);

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const selStr   = format(selDate, "yyyy-MM-dd");
  const days     = weekDays(anchor);

  const dayBookings = useMemo(
    () => bookings.filter((b) => b.scheduleDate === selStr),
    [bookings, selStr]
  );

  const colBookings = useMemo(() => {
    const map = {};
    stylists.forEach((st) => { map[st.id] = []; });
    dayBookings.forEach((b) => {
      const ids = b.stylistIds?.length ? b.stylistIds : [b.stylistId].filter(Boolean);
      ids.forEach((sid) => { if (map[sid]) map[sid].push(b); });
    });
    return map;
  }, [dayBookings, stylists]);

  const weekCounts = useMemo(() => {
    const m = {};
    days.forEach((d) => {
      const ds = format(d, "yyyy-MM-dd");
      m[ds] = bookings.filter((b) => b.scheduleDate === ds).length;
    });
    return m;
  }, [bookings, days]);

  const stats = useMemo(() => {
    const total  = dayBookings.length;
    const served = dayBookings.filter((b) => b.status === "Completed").length;
    const queue  = dayBookings.filter((b) => {
      const s = computeStatus(b, selStr);
      return s === "Accepted" || s === "Pending" || s === "In Progress";
    }).length;
    const avgT = total
      ? Math.round(dayBookings.reduce((s, b) => s + apptDuration(b), 0) / total)
      : 0;
    return { total, served, queue, avgT };
  }, [dayBookings, selStr]);

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const nowY   = selStr === todayStr ? nowMin * MIN_PX : null;

  useEffect(() => {
    if (loading || !gridRef.current) return;
    setTimeout(() => {
      if (!gridRef.current) return;
      gridRef.current.scrollTop = nowY !== null
        ? Math.max(nowY - 120, 0)
        : Math.max(openMin * MIN_PX - 40, 0);
    }, 300);
  }, [loading, openMin, nowY]);

  // ── Auto-scroll horizontal view to salon open time ───────────────────────
  useEffect(() => {
    if (!transposed || !hScrollRef.current) return;
    const target = Math.max(0, openMin * H_MIN_PX - 60);
    hScrollRef.current.scrollLeft = target;
  }, [transposed, openMin]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const prevWeek    = () => setAnchor((d) => subDays(d, 7));
  const nextWeek    = () => setAnchor((d) => addDays(d, 7));
  const goToday     = () => { const t = new Date(); setAnchor(t); setSelDate(t); };
  const manualRefresh = () => setRefreshKey((k) => k + 1);

  const openActionModal = (b) => {
    setTooltip(null);
    setActionModal({ b, status: computeStatus(b, selStr) });
  };

  // ── Column click → open call booking modal ─────────────────────────────
  const handleColClick = (e, stylist) => {
    if (e.target !== e.currentTarget) return; // clicked on appointment, not background
    const colRect   = e.currentTarget.getBoundingClientRect();
    const absoluteY = Math.max(0, e.clientY - colRect.top);
    let   clickedMin = Math.round(Math.floor(absoluteY / MIN_PX) / 15) * 15;
    clickedMin = Math.min(clickedMin, 23 * 60 + 45);
    const h = String(Math.floor(clickedMin / 60)).padStart(2, "0");
    const m = String(clickedMin % 60).padStart(2, "0");
    // Show type picker instead of opening call modal directly
    setBookingTypePicker({ stylist, time: `${h}:${m}`, date: selStr });
  };

  const openConfirm = (action, b) => {
    const MAP = {
      Accepted:  { title: "Accept Booking",    msg: "Are you sure you want to accept this appointment?" },
      Cancelled: { title: "Cancel Booking",    msg: "Are you sure you want to cancel this appointment?" },
      Completed: { title: "Mark as Completed", msg: "Are you sure you want to mark this booking as done?" },
    };
    setConfirmModal({ action, b, ...MAP[action] });
  };

  const updateBookingStatus = async (b, newStatus, extraFields = {}) => {
    setSaving(true);
    const docId = b._docId || b.id || b.bookingId;
    try {
      // Base update payload
      const updatePayload = {
        status:    newStatus,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: "vendor",
        ...extraFields,
      };

      // For Completed: fetch vendorPhone from vendors collection
      if (newStatus === "Completed") {
        try {
          const vSnap = await db().collection("vendors").doc(vendorId).get();
          if (vSnap.exists) {
            const vData = vSnap.data();
            updatePayload.vendorPhone = vData.phoneNumber || vData.phone || "";
          }
        } catch (_) { /* non-fatal */ }
      }

      await db().collection("appointments").doc(docId).update(updatePayload);

      const mergedFields = { status: newStatus, ...extraFields };
      setBookings((prev) =>
        prev.map((bk) =>
          (bk._docId === docId || bk.id === docId) ? { ...bk, ...mergedFields } : bk
        )
      );
      // Update action modal's displayed status
      if (actionModal && (actionModal.b._docId === docId || actionModal.b.id === docId)) {
        setActionModal((prev) => ({
          ...prev,
          b: { ...prev.b, ...mergedFields },
          status: computeStatus({ ...prev.b, ...mergedFields }, selStr),
        }));
      }
      // Auto-send SMS + email on status change (mirrors Flutter OrderCard)
      sendSmsToUser(b, newStatus).catch(() => {});
      sendEmailOnStatusChange(b, newStatus).catch(() => {});
      setNotification({ msg: `Appointment ${newStatus.toLowerCase()} successfully`, type: "success" });
    } catch (e) {
      setNotification({ msg: "Update failed. Please try again.", type: "error" });
    } finally {
      setSaving(false);
      setConfirmModal(null);
      setPaymentModal(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="sc_page">

      {/* ── Stats bar ──────────────────────────────────────────────── */}
      <div className="sc_stats_bar">
        {[
          { ic: "fa-calendar",     val: stats.total,       lbl: "Total",    mod: "sc_ic_total"  },
          { ic: "fa-check-circle", val: stats.served,      lbl: "Served",   mod: "sc_ic_served" },
          { ic: "fa-users",        val: stats.queue,       lbl: "Queue",    mod: "sc_ic_queue"  },
          { ic: "fa-clock-o",      val: `${stats.avgT} m`, lbl: "Avg Time", mod: "sc_ic_time"   },
        ].map((s) => (
          <div key={s.lbl} className="sc_stat_card">
            <div className={`sc_stat_icon_wrap ${s.mod}`}><i className={`fa ${s.ic}`}></i></div>
            <div>
              <div className="sc_stat_val">{s.val}</div>
              <div className="sc_stat_lbl">{s.lbl}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Controls ───────────────────────────────────────────────── */}
      <div className="sc_controls">
        <div className="sc_nav_grp">
          <button className="sc_nav_btn" onClick={prevWeek}><i className="fa fa-chevron-left"></i></button>
          <button className="sc_today_btn" onClick={goToday}>Today</button>
          <button className="sc_nav_btn" onClick={nextWeek}><i className="fa fa-chevron-right"></i></button>
        </div>
        <span className="sc_week_label">
          {format(days[0], "d MMM")} \u2013 {format(days[6], "d MMM yyyy")}
        </span>
        <button className="sc_refresh_btn" onClick={manualRefresh} title="Refresh now">
          <i className="fa fa-refresh"></i>
        </button>
        <button
          className={`sc_transpose_btn${transposed ? " active" : ""}`}
          onClick={() => setTransposed((v) => !v)}
          title={transposed ? "Switch to vertical view" : "Switch to horizontal view"}
        >
          <i className={`fa ${transposed ? "fa-toggle-on" : "fa-toggle-off"}`}></i>
          {transposed ? "Vertical" : "Horizontal"}
        </button>
        <button
          className="sc_book_call_btn"
          onClick={() => {
            const now = new Date();
            const h   = String(now.getHours()).padStart(2, "0");
            const m   = String(Math.round(now.getMinutes() / 15) * 15 % 60).padStart(2, "0");
            setCallModal({ stylist: stylists[0] || null, time: `${h}:${m}`, date: selStr });
          }}
          title="Book a call appointment"
        >
          <i className="fa fa-phone"></i> Book Call
        </button>
        <div className="sc_legend">
          {LEGEND.map((l) => (
            <span key={l.key} className="sc_legend_item">
              <span className="sc_legend_dot" style={{ background: l.dot }}></span>
              {l.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Week strip ─────────────────────────────────────────────── */}
      <div className="sc_week_strip">
        {days.map((day) => {
          const ds = format(day, "yyyy-MM-dd");
          return (
            <div
              key={ds}
              className={`sc_wd${ds === selStr ? " sc_wd_sel" : ""}${ds === todayStr ? " sc_wd_today" : ""}`}
              onClick={() => setSelDate(new Date(ds + "T12:00:00"))}
            >
              <div className="sc_wd_name">{format(day, "EEE")}</div>
              <div className="sc_wd_num">{format(day, "d")}</div>
              {weekCounts[ds] > 0 && <div className="sc_wd_badge">{weekCounts[ds]}</div>}
            </div>
          );
        })}
      </div>

      {/* ── Grid ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="sc_loading"><i className="fa fa-spinner fa-pulse fa-2x"></i></div>
      ) : stylists.length === 0 ? (
        <div className="sc_empty">No active stylists found.</div>
      ) : transposed ? (
        /* ── Horizontal Gantt view ───────────────────────────────────── */
        <div className="sc_grid_outer sc_grid_outer_h" ref={hScrollRef}>
          {/* Sticky time header */}
          <div className="sc_h_hdr">
            <div className="sc_h_corner" style={{ width: SNAME_W, height: H_HDR_H }}></div>
            <div className="sc_h_lane_hdr" style={{ width: TOTAL_MINS * H_MIN_PX }}>
              {hourSlots.map((sm) => (
                <div
                  key={sm}
                  className="sc_h_time_lbl"
                  style={{ left: sm * H_MIN_PX, width: H_HOUR_W }}
                >
                  {fmt12(String(sm / 60 | 0).padStart(2, "0") + ":00")}
                </div>
              ))}
              {/* Salon open line in header */}
              <div className="sc_h_open_tick" style={{ left: openMin * H_MIN_PX }} />
              {/* Salon close line in header */}
              <div className="sc_h_close_tick" style={{ left: closeMin * H_MIN_PX }} />
            </div>
          </div>

          {/* Stylist rows */}
          {stylists.map((st) => (
            <div key={st.id} className="sc_h_row" style={{ height: ROW_H }}>
              {/* Sticky name cell */}
              <div className="sc_h_sname_cell" style={{ width: SNAME_W, height: ROW_H }}>
                <div className="sc_hdr_avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                  {st.image
                    ? <img src={st.image} alt={st.name} />
                    : <span>{(st.name || "?")[0].toUpperCase()}</span>}
                </div>
                <span className="sc_h_sname_lbl">{st.name}</span>
              </div>

              {/* Time lane */}
              <div
                className="sc_h_lane"
                style={{ width: TOTAL_MINS * H_MIN_PX, height: ROW_H }}
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  let mins = Math.round((e.clientX - rect.left) / H_MIN_PX / 15) * 15;
                  mins = Math.min(Math.max(mins, 0), 23 * 60 + 45);
                  setCallModal({ stylist: st, time: `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`, date: selStr });
                }}
              >
                {/* Off-hours shading before open */}
                {openMin > 0 && (
                  <div className="sc_h_off_band" style={{ left: 0, width: openMin * H_MIN_PX }} />
                )}
                {/* Off-hours shading after close */}
                {closeMin < TOTAL_MINS && (
                  <div className="sc_h_off_band" style={{ left: closeMin * H_MIN_PX, width: (TOTAL_MINS - closeMin) * H_MIN_PX }} />
                )}
                {/* Salon open marker */}
                <div className="sc_h_open_line" style={{ left: openMin * H_MIN_PX }} />
                {/* Salon close marker */}
                <div className="sc_h_close_line" style={{ left: closeMin * H_MIN_PX }} />
                {/* Hour lines */}
                {hourSlots.map((sm) => (
                  <div key={sm} className="sc_h_vline" style={{ left: sm * H_MIN_PX }} />
                ))}
                {/* Half-hour lines */}
                {hourSlots.map((sm) => (
                  <div key={"hf" + sm} className="sc_h_hf_vline" style={{ left: (sm + 30) * H_MIN_PX }} />
                ))}
                {/* Current time vertical line */}
                {selStr === todayStr && (
                  <div className="sc_h_now_vline" style={{ left: nowMin * H_MIN_PX }} />
                )}
                {/* Appointment blocks */}
                {(colBookings[st.id] || []).map((b, bi) => {
                  const status = computeStatus(b, selStr);
                  const cfg    = STATUS_CFG[status] || STATUS_CFG.Accepted;
                  const startM = toMin(b.scheduleTime);
                  const dur    = apptDuration(b);
                  const left   = startM * H_MIN_PX;
                  const width  = Math.max(dur * H_MIN_PX, 36);
                  const svcNames = (b.services || []).map((sv) => sv.serviceName || sv.name).filter(Boolean).join(", ");
                  return (
                    <div
                      key={b.bookingId || b._docId || bi}
                      className="sc_h_appt"
                      style={{
                        left,
                        width,
                        background: cfg.bg,
                        borderLeft: `4px solid ${cfg.border}`,
                        color: cfg.color,
                      }}
                      onMouseEnter={(e) => setTooltip({ b, status, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={(e) => { e.stopPropagation(); openActionModal(b); }}
                    >
                      <div className="sc_h_appt_name">{b.userName || "—"}</div>
                      {width > 60 && (
                        <div className="sc_h_appt_time">{fmt12(b.scheduleTime)} – {fmt12(addMinutes(b.scheduleTime, dur))}</div>
                      )}
                      {width > 100 && svcNames && (
                        <div className="sc_h_appt_svc">{svcNames}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="sc_grid_outer" ref={gridRef}>
          <div className="sc_grid_inner" style={{ minWidth: TIME_W + stylists.length * COL_W }}>

            {/* Sticky stylist header */}
            <div className="sc_hdr_row">
              <div className="sc_hdr_spacer" style={{ width: TIME_W }}></div>
              {stylists.map((st) => {
                const sb   = colBookings[st.id] || [];
                const q    = sb.filter((b) => {
                  const s = computeStatus(b, selStr);
                  return s === "Accepted" || s === "Pending" || s === "In Progress";
                }).length;
                const done = sb.filter((b) => b.status === "Completed").length;
                return (
                  <div key={st.id} className="sc_hdr_col" style={{ width: COL_W }}>
                    <div className="sc_hdr_avatar">
                      {st.image
                        ? <img src={st.image} alt={st.name} />
                        : <span>{(st.name || "?")[0].toUpperCase()}</span>}
                    </div>
                    <div className="sc_hdr_info">
                      <div className="sc_hdr_name">{st.name}</div>
                      <div className="sc_hdr_meta">
                        <span className="sc_badge_in">In</span>
                        {q    > 0 && <span className="sc_badge_q">{q}&nbsp;queued</span>}
                        {done > 0 && <span className="sc_badge_done">{done}&nbsp;done</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Body */}
            <div className="sc_body" style={{ height: totalH }}>

              {/* Time column */}
              <div className="sc_time_col" style={{ width: TIME_W }}>
                {openMin > 0 && (
                  <div className="sc_time_off" style={{ top: 0, height: openMin * MIN_PX }} />
                )}
                {closeMin < TOTAL_MINS && (
                  <div className="sc_time_off" style={{ top: closeMin * MIN_PX, height: (TOTAL_MINS - closeMin) * MIN_PX }} />
                )}
                {hourSlots.map((sm) => (
                  <div
                    key={sm}
                    className={"sc_time_lbl" + (sm < openMin || sm >= closeMin ? " sc_time_lbl_off" : "")}
                    style={{ top: sm * MIN_PX }}
                  >
                    {fmt12(String(sm / 60 | 0).padStart(2, "0") + ":00")}
                  </div>
                ))}
                <div className="sc_work_tick sc_work_open_tick"  style={{ top: openMin  * MIN_PX }} />
                <div className="sc_work_tick sc_work_close_tick" style={{ top: closeMin * MIN_PX }} />
              </div>

              {/* Appointment columns */}
              <div className="sc_cols_area">
                {openMin > 0 && (
                  <div className="sc_off_band" style={{ top: 0, height: openMin * MIN_PX }} />
                )}
                {closeMin < TOTAL_MINS && (
                  <div className="sc_off_band" style={{ top: closeMin * MIN_PX, height: (TOTAL_MINS - closeMin) * MIN_PX }} />
                )}
                <div className="sc_work_open_line"  style={{ top: openMin  * MIN_PX }} />
                <div className="sc_work_close_line" style={{ top: closeMin * MIN_PX }} />
                {nowY !== null && (
                  <div className="sc_now_line" style={{ top: nowY }}>
                    <div className="sc_now_dot"></div>
                  </div>
                )}

                {stylists.map((st) => (
                  <div key={st.id} className="sc_col" style={{ width: COL_W, cursor: "crosshair" }}
                    onClick={(e) => handleColClick(e, st)}
                  >
                    {hourSlots.map((sm) => (
                      <div key={sm} className="sc_hr_line" style={{ top: sm * MIN_PX }} />
                    ))}
                    {hourSlots.map((sm) => (
                      <div key={"hf" + sm} className="sc_hf_line" style={{ top: (sm + 30) * MIN_PX }} />
                    ))}
                    {(() => {
                      const bkgs = colBookings[st.id] || [];
                      const overlapMap = computeOverlapLayout(bkgs);
                      const GUTTER = 3;
                      const usableW = COL_W - GUTTER * 2;
                      return bkgs.map((b, bi) => {
                        const key    = b.bookingId || b._docId || String(bi);
                        const lay    = overlapMap.get(key) || { col: 0, colCount: 1 };
                        const slotW  = Math.floor(usableW / lay.colCount);
                        const blkW   = slotW - (lay.colCount > 1 ? 2 : 0);
                        const blkL   = GUTTER + lay.col * slotW;
                        const status = computeStatus(b, selStr);
                        const cfg    = STATUS_CFG[status] || STATUS_CFG.Accepted;
                        const startM = toMin(b.scheduleTime);
                        const dur    = apptDuration(b);
                        const top    = startM * MIN_PX;
                        const h      = Math.max(dur * MIN_PX, MIN_BLOCK);
                        const endT   = addMinutes(b.scheduleTime, dur);
                        const svcs   = (b.services || []).map((s) => s.name).join(", ");
                        return (
                          <div
                            key={key}
                            className="sc_appt"
                            style={{
                              top, height: h,
                              left: blkL, width: blkW,
                              right: "unset",
                              background: cfg.bg,
                              borderLeft: `3px solid ${cfg.border}`,
                              color: cfg.color,
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => setTooltip({ b, status, x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setTooltip(null)}
                            onClick={(e) => { e.stopPropagation(); openActionModal(b); }}
                          >
                            <div className="sc_appt_name">{b.userName || "—"}</div>
                            <div className="sc_appt_time">{fmt12(b.scheduleTime)} – {fmt12(endT)}</div>
                            {h >= 55 && svcs && <div className="sc_appt_svcs">{svcs}</div>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Hover tooltip ──────────────────────────────────────────── */}
      {tooltip && (() => {
        const tb = tooltip.b;
        const ttBreakdown = tb.stylistBreakdown || {};
        const ttIsMulti = Object.keys(ttBreakdown).length > 1 ||
          (tb.services || []).some((s) => s.stylistId && s.stylistId !== tb.stylistId);
        const ttSvcsByStylist = {};
        (tb.services || []).forEach((s) => {
          const sid = s.stylistId || tb.stylistId || "unknown";
          const snm = s.stylistName || tb.stylistName || "—";
          if (!ttSvcsByStylist[sid]) ttSvcsByStylist[sid] = { name: snm, services: [] };
          ttSvcsByStylist[sid].services.push(s);
        });
        return (
          <div className="sc_tooltip" style={{ left: tooltip.x + 16, top: tooltip.y - 10 }}>
            <div className="sc_tt_name">{tb.userName}</div>
            <div className="sc_tt_row"><span>Time</span>{fmt12(tb.scheduleTime)}</div>
            {ttIsMulti ? (
              <div className="sc_tt_multi">
                {Object.entries(ttSvcsByStylist).map(([sid, grp]) => (
                  <div key={sid} className="sc_tt_multi_row">
                    <span className="sc_tt_stylist_nm">{grp.name}</span>
                    <span>{grp.services.map((s) => s.name || s.serviceName).join(", ")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="sc_tt_row"><span>Stylist</span>{tb.stylistName || "—"}</div>
                {(tb.services || []).length > 0 && (
                  <div className="sc_tt_row"><span>Services</span>
                    {(tb.services || []).map((s) => s.name).filter(Boolean).join(" · ")}
                  </div>
                )}
              </>
            )}
            <div className="sc_tt_row"><span>Amount</span>₹{tb.finalPrice}</div>
            <div
              className="sc_tt_status"
              style={{ background: STATUS_CFG[tooltip.status]?.bg, color: STATUS_CFG[tooltip.status]?.border }}
            >
              {STATUS_CFG[tooltip.status]?.label || tooltip.status}
            </div>
            <div className="sc_tt_hint">Click for actions</div>
          </div>
        );
      })()}

      {/* ── Action Modal ───────────────────────────────────────────── */}
      {actionModal && (() => {
        const b      = actionModal.b;
        const status = computeStatus(b, selStr); // live-recompute in case just updated
        const cfg    = STATUS_CFG[status] || STATUS_CFG.Accepted;
        const svcs   = (b.services || []).map((s) => s.name).join(", ");
        const dur    = apptDuration(b);
        const endT   = addMinutes(b.scheduleTime, dur);
        const okMark = canMarkDone(b);

        // Build per-stylist service groups for multi-stylist appointments
        const breakdown = b.stylistBreakdown || {};
        const isMultiStylist = Object.keys(breakdown).length > 1 ||
          (b.services || []).some((s) => s.stylistId && s.stylistId !== b.stylistId);
        // Group services by stylist
        const svcsByStylist = {};
        (b.services || []).forEach((s) => {
          const sid  = s.stylistId  || b.stylistId  || "unknown";
          const snm  = s.stylistName || b.stylistName || "—";
          if (!svcsByStylist[sid]) svcsByStylist[sid] = { name: snm, services: [] };
          svcsByStylist[sid].services.push(s);
        });
        const stylistGroups = Object.entries(svcsByStylist);
        return (
          <div className="sc_modal_overlay" onClick={() => setActionModal(null)}>
            <div className="sc_modal" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="sc_modal_header_row">
                <button className="sc_modal_close" onClick={() => setActionModal(null)}>×</button>
                <span className="sc_modal_title">Appointment</span>
                <span
                  className="sc_modal_status_pill"
                  style={{ background: cfg.bg, color: cfg.border, border: `1px solid ${cfg.border}` }}
                >
                  {cfg.label}
                </span>
              </div>

              {/* Body: Flutter card layout (time|divider|details) */}
              <div className="sc_modal_body">
                {/* Left: time + date */}
                <div className="sc_modal_left">
                  <div className="sc_modal_time">{fmt12(b.scheduleTime)}</div>
                  <div className="sc_modal_time_end">– {fmt12(endT)}</div>
                  <div className="sc_modal_date">{fmtDate(b.scheduleDate)}</div>
                  <div className="sc_modal_dur">{dur} min</div>
                </div>
                <div className="sc_modal_vert_div"></div>
                {/* Right: details */}
                <div className="sc_modal_right">
                  <div className="sc_modal_customer">{b.userName || "—"}</div>
                  {b.phone && <div className="sc_modal_detail"><i className="fa fa-phone" style={{marginRight:4}}></i>{b.phone}</div>}

                  {/* Multi-stylist breakdown */}
                  {isMultiStylist ? (
                    <div className="sc_modal_stylist_breakdown">
                      {stylistGroups.map(([sid, grp]) => (
                        <div key={sid} className="sc_modal_stylist_row">
                          <div className="sc_modal_stylist_name">
                            <i className="fa fa-scissors" style={{marginRight:5,fontSize:10}}></i>
                            {grp.name}
                          </div>
                          <div className="sc_modal_stylist_svcs">
                            {grp.services.map((s, i) => (
                              <div key={i} className="sc_modal_svc_line">
                                <span className="sc_modal_svc_name">{s.name || s.serviceName || "—"}</span>
                                {s.time > 0 && (
                                  <span className="sc_modal_svc_time">{s.time} min</span>
                                )}
                                {s.price > 0 && (
                                  <span className="sc_modal_svc_price">₹{s.price}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="sc_modal_detail">
                        <i className="fa fa-scissors" style={{marginRight:4,fontSize:10}}></i>
                        {b.stylistName || "—"}
                      </div>
                      {svcs && <div className="sc_modal_detail">{svcs}</div>}
                    </>
                  )}

                  {b.finalPrice && (
                    <div className="sc_modal_amount">₹ {b.finalPrice}</div>
                  )}
                </div>
              </div>

              <hr className="sc_modal_hr" />

              {/* Action buttons (mirrors Flutter card) */}
              <div className="sc_modal_actions">
                {/* Cancel */}
                {status !== "Completed" && status !== "Cancelled" && (
                  <button
                    className="sc_modal_btn sc_modal_btn_cancel"
                    onClick={() => openConfirm("Cancelled", b)}
                  >
                    <i className="fa fa-times"></i> Cancel Appointment
                  </button>
                )}
                {/* Accept (Pending only) */}
                {(status === "Pending") && (
                  <button
                    className="sc_modal_btn sc_modal_btn_accept"
                    onClick={() => openConfirm("Accepted", b)}
                  >
                    <i className="fa fa-check"></i> Accept
                  </button>
                )}
                {/* Mark Done (Accepted + time passed) — opens payment modal */}
                {(status === "Accepted" || status === "In Progress") && (
                  <button
                    className={`sc_modal_btn sc_modal_btn_done${okMark ? "" : " sc_modal_btn_disabled"}`}
                    disabled={!okMark}
                    onClick={() => {
                      if (!okMark) return;
                      setActionModal(null);
                      setPaymentModal({ b });
                    }}
                    title={okMark ? "" : "Available after appointment start time"}
                  >
                    <i className="fa fa-flag-checkered"></i> Mark Done
                  </button>
                )}
              </div>


            </div>
          </div>
        );
      })()}

      {/* ── Confirm dialog ─────────────────────────────────────────── */}
      {confirmModal && (
        <div className="sc_confirm_overlay">
          <div className="sc_confirm">
            <div className="sc_confirm_title">{confirmModal.title}</div>
            <div className="sc_confirm_msg">{confirmModal.msg}</div>
            <div className="sc_confirm_actions">
              <button
                className="sc_confirm_btn sc_confirm_cancel"
                onClick={() => setConfirmModal(null)}
                disabled={saving}
              >
                No
              </button>
              <button
                className={`sc_confirm_btn sc_confirm_ok${
                  confirmModal.action === "Cancelled" ? " sc_confirm_ok_red"
                  : confirmModal.action === "Accepted" ? " sc_confirm_ok_green"
                  : " sc_confirm_ok_orange"
                }`}
                onClick={() => updateBookingStatus(confirmModal.b, confirmModal.action)}
                disabled={saving}
              >
                {saving
                  ? <i className="fa fa-spinner fa-pulse"></i>
                  : confirmModal.action === "Accepted" ? "Accept"
                  : confirmModal.action === "Cancelled" ? "Cancel It"
                  : "Mark Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notification ─────────────────────────────────────── */}
      {notification && (
        <div className={`sc_notification sc_notif_${notification.type}`}>
          <i className={`fa ${
            notification.type === "success" ? "fa-check-circle"
            : notification.type === "error" ? "fa-exclamation-circle"
            : "fa-info-circle"
          }`}></i>
          &nbsp;{notification.msg}
        </div>
      )}

      {/* ── Payment Modal (Mark Done) ──────────────────────────────── */}
      {paymentModal && (() => {
        const b = paymentModal.b;
        const existingMode = b.paymentMode || "Cash";
        return (
          <PaymentConfirmModal
            booking={b}
            initialMode={existingMode}
            saving={saving}
            onCancel={() => setPaymentModal(null)}
            onConfirm={(payMode) =>
              updateBookingStatus(b, "Completed", {
                paymentMode:   payMode,
                paymentStatus: "Paid",
              })
            }
          />
        );
      })()}

      {/* ── Booking Type Picker ────────────────────────────────────── */}
      {bookingTypePicker && (
        <div className="sc_btp_backdrop" onClick={() => setBookingTypePicker(null)}>
          <div className="sc_btp_modal" onClick={(e) => e.stopPropagation()}>
            <div className="sc_btp_title">Add Appointment</div>
            <p className="sc_btp_sub">
              {bookingTypePicker.date} &nbsp;·&nbsp; {fmt12(bookingTypePicker.time)}
              {bookingTypePicker.stylist && ` · ${bookingTypePicker.stylist.name}`}
            </p>
            <div className="sc_btp_btns">
              <button
                className="sc_btp_btn sc_btp_walkin"
                onClick={() => {
                  setBookingTypePicker(null);
                  setShowWalkInModal(true);
                }}
              >
                <i className="fa fa-male"></i>
                <span>Walk-in</span>
                <small>Customer is present</small>
              </button>
              <button
                className="sc_btp_btn sc_btp_call"
                onClick={() => {
                  setCallModal(bookingTypePicker);
                  setBookingTypePicker(null);
                }}
              >
                <i className="fa fa-phone"></i>
                <span>Call Booking</span>
                <small>Appointment over phone</small>
              </button>
            </div>
            <button className="sc_btp_cancel" onClick={() => setBookingTypePicker(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Walk-in Modal ─────────────────────────────────────────────── */}
      {showWalkInModal && (
        <div className="sc_btp_backdrop">
          <div className="sc_walkin_modal">
            <WalkInForm onClose={() => setShowWalkInModal(false)} />
          </div>
        </div>
      )}

      {/* ── Call Booking Modal ─────────────────────────────────────── */}
      {callModal && (
        <CallBookingModal
          vendorId={vendorId}
          allStylists={stylists}
          initialStylist={callModal.stylist}
          initialDate={callModal.date}
          initialTime={callModal.time}
          existingBookings={bookings}
          onClose={() => setCallModal(null)}
          onSaved={(newAppt) => {
            setBookings((prev) => [...prev, newAppt]);
            setCallModal(null);
            setNotification({ msg: "Call appointment booked successfully!", type: "success" });
          }}
        />
      )}
    </div>
  );
};

export default StylistCalendar;
